import { PropsWithChildren, useCallback, useMemo } from "react";
import { TagGroupContext } from "./TagGroupContext";
import {
  FilterFindManyTagGroupInput,
  FilterFindOneTagGroupInput,
  useTagGroupManyQuery,
} from "gql/generated";

export const TagGroupContextProvider = ({ children }: PropsWithChildren) => {
  const { data, loading, error } = useTagGroupManyQuery();

  /**
   * Get a single tag group by ID, key, and type
   */
  const getTagGroup = useCallback(
    (filter: FilterFindOneTagGroupInput) =>
      data?.tagGroupMany.find(tagGroup => {
        let isIncluded = true;
        if (filter._id) {
          isIncluded = filter._id === tagGroup._id;
        }
        if (isIncluded && filter.key) {
          isIncluded = filter.key === tagGroup.key;
        }
        if (isIncluded && filter.type) {
          isIncluded = filter.type.includes(tagGroup.type);
        }
        return isIncluded;
      }) ?? undefined,
    [data],
  );

  /**
   * Filter the tag groups by key and type
   */
  const getTagGroups = useCallback(
    (filter: FilterFindManyTagGroupInput) =>
      data?.tagGroupMany.filter(tagGroup => {
        let isIncluded = true;
        if (filter.key) {
          isIncluded = filter.key === tagGroup.key;
        }
        if (isIncluded && filter.type) {
          isIncluded = filter.type.includes(tagGroup.type);
        }
        return isIncluded;
      }) ?? [],
    [data],
  );

  /**
   * Filter by tag group IDs
   */
  const getTagGroupsByIds = useCallback(
    (ids: string[]) => {
      return (
        data?.tagGroupMany.filter(tagGroup => ids.includes(tagGroup._id)) ?? []
      );
    },
    [data],
  );

  const value = useMemo(
    () => ({
      tagGroups: data?.tagGroupMany ?? [],
      loading,
      error,
      getTagGroup,
      getTagGroups,
      getTagGroupsByIds,
    }),
    [data, loading, error, getTagGroup, getTagGroups, getTagGroupsByIds],
  );

  return (
    <TagGroupContext.Provider value={value}>
      {children}
    </TagGroupContext.Provider>
  );
};
